import { Link } from 'expo-router';
import Star from 'lucide-react-native/icons/star';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { CardImage } from '@/components/riviera/card-image';
import { Badge } from '@/components/ui/badge';
import { Fonts, Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { boatCardImage, type Boat } from '@todo/core/boats';
import { localizedBoatTitle, localizedBoatType } from '@/lib/i18n/boat-content';
import { currencySymbol } from '@todo/core/i18n/format';
import { useLocale } from '@todo/core/i18n/use-locale';
import { cities } from '@/lib/events-data';

type Translate = (key: string, options?: Record<string, unknown>) => string;

function formatPrice(min: number | null, currency: string | null, t: Translate) {
  if (min == null) return '';
  const symbol = currencySymbol(currency);
  return t('common.fromPrice', { price: `${symbol}${min}` });
}

// Tapping a card opens the in-app detail screen (/[lang]/boats/[id]); the
// actual booking (and our affiliate commission) happens from there, out on
// Click&Boat.
export function BoatCard({ boat, eager = false }: { boat: Boat; eager?: boolean }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const cityName = boat.city_id ? (cities.find((c) => c.id === boat.city_id)?.name ?? null) : null;

  return (
    <Link
      href={{ pathname: '/[lang]/boats/[id]', params: { lang: locale, id: boat.id } }}
      asChild>
      <Pressable
        style={({ pressed }) => [styles.cardPressable, pressed && { opacity: 0.85 }]}>
      <View style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <View style={styles.imageBox}>
          <CardImage
            uri={boatCardImage(boat.image_url)}
            alt={localizedBoatTitle(boat, locale)}
            eager={eager}
          />
          <View style={styles.badgePos}>
            <Badge variant="gold">{t('boats.badge')}</Badge>
          </View>
        </View>
        <View style={styles.body}>
          <Text
            numberOfLines={2}
            style={[styles.title, { color: theme.text, fontFamily: Fonts?.serif }]}>
            {localizedBoatTitle(boat, locale)}
          </Text>
          <View style={styles.metaRow}>
            <Text style={[styles.metaText, { color: theme.mutedForeground }]}>
              {localizedBoatType(boat, locale)}
              {cityName ? ` · ${cityName}` : ''}
            </Text>
          </View>
          {boat.rating != null ? (
            <View style={styles.metaRow}>
              <Star size={13} color={theme.gold} fill={theme.gold} />
              <Text style={[styles.metaText, { color: theme.mutedForeground }]}>
                {boat.rating.toFixed(1)}
              </Text>
            </View>
          ) : null}
          <View style={styles.footerRow}>
            <Text style={[styles.price, { color: theme.gold }]}>
              {formatPrice(boat.from_price, boat.currency, t)}
            </Text>
            <Text style={[styles.attribution, { color: theme.mutedForeground }]}>
              {t('boats.viaClickAndBoat')}
            </Text>
          </View>
        </View>
      </View>
      </Pressable>
    </Link>
  );
}

const styles = StyleSheet.create({
  cardPressable: {
    width: 240,
    flexGrow: 0,
    flexShrink: 0,
  },
  card: {
    width: 240,
    borderRadius: Radius.xl,
    borderWidth: 1,
    overflow: 'hidden',
  },
  imageBox: {
    height: 140,
    width: '100%',
    position: 'relative',
  },
  badgePos: {
    position: 'absolute',
    top: Spacing.two,
    left: Spacing.two,
  },
  body: {
    padding: Spacing.three - 2,
    gap: 4,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20,
    minHeight: 40,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaText: {
    fontSize: 12,
  },
  footerRow: {
    marginTop: 4,
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  price: {
    fontSize: 13,
    fontWeight: '700',
  },
  attribution: {
    fontSize: 10,
    fontWeight: '500',
  },
});
